import type { MouseEvent as ReactMouseEvent } from "react";

import type { SkillMarkdownPreviewCacheEntry } from "./skill-markdown-preview-cache";
import { SkillMarkdownLinkPreviewLayer } from "./SkillMarkdownLinkPreviewLayer";
import { SkillMarkdownPreviewContent } from "./SkillMarkdownPreviewContent";
import {
  isSkillMarkdownRelativeLink,
  resolveSkillMarkdownRelativePath,
} from "./utils";

import "./skill-markdown-editor.css";

export interface SkillMarkdownPreviewProps {
  markdown: string;
  className?: string;
  filePath?: string | null;
  onNavigateToFile?: (path: string) => void;
  hasLinkedFile?: (path: string) => boolean;
  getLinkedFileContent?: (path: string) => string | undefined;
  markdownPreparse?: SkillMarkdownPreviewCacheEntry | null;
}

export const SkillMarkdownPreview = ({
  markdown,
  className,
  filePath,
  onNavigateToFile,
  hasLinkedFile,
  getLinkedFileContent,
  markdownPreparse,
}: SkillMarkdownPreviewProps) => {
  const handleMarkdownClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!onNavigateToFile) {
      return;
    }

    const target = (event.target as HTMLElement).closest("a");
    if (!target) {
      return;
    }

    const href = target.getAttribute("href");
    if (!href || !isSkillMarkdownRelativeLink(href)) {
      return;
    }

    const targetPath = resolveSkillMarkdownRelativePath(filePath ?? null, href);
    if (!targetPath) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onNavigateToFile(targetPath);
  };

  return (
    <SkillMarkdownLinkPreviewLayer
      filePath={filePath}
      hasLinkedFile={hasLinkedFile}
      getLinkedFileContent={getLinkedFileContent}
    >
      <SkillMarkdownPreviewContent
        markdown={markdown}
        className={className}
        markdownPreparse={markdownPreparse}
        onBodyClick={handleMarkdownClick}
      />
    </SkillMarkdownLinkPreviewLayer>
  );
};

export { SkillMarkdownLinkPreviewLayer } from "./SkillMarkdownLinkPreviewLayer";
export type { SkillMarkdownLinkPreviewLayerProps } from "./SkillMarkdownLinkPreviewLayer";
export { buildSkillMarkdownPreviewCache } from "./skill-markdown-preview-cache";
export type { SkillMarkdownPreviewCacheEntry } from "./skill-markdown-preview-cache";
